import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { buildLeaveDayMap, fetchEmployeeLeaves } from '../../../utils/employeeLeaves';

/**
 * What the personal calendar shows: the employee's visits, their own jobs
 * (CalendarJobs), approved leaves and the projects they work on.
 */
const useCalendarData = (employeeId) => {
  const [visits, setVisits] = useState([]);
  const [calendarJobs, setCalendarJobs] = useState([]);
  // Onaylanmış izinler: 'yyyy-mm-dd' → o güne düşen izin kayıtları
  const [leaveDayMap, setLeaveDayMap] = useState(() => new Map());
  const [userProjects, setUserProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVisits = useCallback(async () => {
    const visitsSnapshot = await getDocs(query(collection(db, 'Visits'), where('employeeId', '==', employeeId)));
    setVisits(visitsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchVisits();

        const jobsSnapshot = await getDocs(query(collection(db, 'CalendarJobs'), where('employeeId', '==', employeeId)));
        setCalendarJobs(jobsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

        // Onaylanmış izinler (İzin Yönetimi kayıtları + onaylanan talepler)
        try {
          const leaves = await fetchEmployeeLeaves(employeeId);
          setLeaveDayMap(buildLeaveDayMap(leaves));
        } catch (leaveError) {
          console.error('Error fetching leaves:', leaveError);
          setLeaveDayMap(new Map());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employeeId, fetchVisits]);

  // Çalışanın atandığı projeler
  useEffect(() => {
    if (!employeeId) return;
    const fetchUserProjects = async () => {
      try {
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const allProjects = projectsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUserProjects(allProjects.filter((project) => project.employees
          && Object.values(project.employees).some((emp) => emp.id === employeeId)));
      } catch (error) {
        console.error('Error fetching user projects:', error);
      }
    };
    fetchUserProjects();
  }, [employeeId]);

  return { visits, setVisits, fetchVisits, calendarJobs, setCalendarJobs, leaveDayMap, userProjects, loading };
};

export default useCalendarData;
